import { cn } from '@/lib/tailwind-utils';

export type PercentageProgressTone = 'green' | 'yellow' | 'red' | 'none';

/** >80 green, >50 yellow, otherwise red; nullish → N/A (grey track only) */
export function getPercentageProgressTone(value: number | null | undefined): PercentageProgressTone {
  if (value == null || Number.isNaN(value)) return 'none';
  if (value > 80) return 'green';
  if (value > 50) return 'yellow';
  return 'red';
}

const toneClassName: Record<PercentageProgressTone, string> = {
  green: 'text-[#0D9488]',
  yellow: 'text-[#D97706]',
  red: 'text-[#EB404A]',
  none: 'text-muted-foreground',
};

const fillClassName: Record<PercentageProgressTone, string> = {
  green: 'bg-[#0D9488]',
  yellow: 'bg-[#D97706]',
  red: 'bg-[#EB404A]',
  none: 'bg-transparent',
};

export interface PercentageProgressProps {
  label: string;
  value: number | null | undefined;
  className?: string;
  'data-testid'?: string;
}

export function PercentageProgress({ label, value, className, 'data-testid': testId }: PercentageProgressProps) {
  const tone = getPercentageProgressTone(value);
  const hasValue = tone !== 'none';
  const clamped = hasValue ? Math.min(100, Math.max(0, value!)) : 0;

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)} data-testid={testId}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-bold text-foreground">{label}</span>
        <span className={cn('font-bold tabular-nums', toneClassName[tone])}>
          {hasValue ? `${Math.round(value!)}%` : 'N/A'}
        </span>
      </div>
      <div className="h-0.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
        <div
          className={cn('h-full rounded-full transition-[width]', fillClassName[tone])}
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
}
